// QR Kiosk v2 — Machine spec sheet (surface 2). v1 MachinePage parity in a
// plate-framed card. Media band uses catalogModel.imageDetailUrl (§4.2 orphan
// surfaced), viewer banner from /profile (PG-4, fails toward hiding), combined
// System row with version-alone fix (PG-5 / bug #9). 8 mono spec rows. Tokens only.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import {
  getMachineDisplayName,
  getBrandDisplayName,
  getMachineName,
  getProductionYearDisplay,
} from "../../../shared/utils/machineUtils";
import { getMachine } from "../../publicSlice";
import publicMachinesApi from "../../api/publicMachinesApi";
import { KPage } from "../shared/KPage";
import { Glyph, GD } from "../shared/glyphs";
import { ErrorCallout } from "../shared/ErrorCallout";
import { CachedErrorState } from "../shared/CachedErrorState";
import { useSurfaceState } from "../shared/useSurfaceState";
import { useKioskRouteMachine } from "../shared/useKioskRouteMachine";
import { formatClockTime } from "../shared/clock";
import { SkeletonBlock } from "../shared/SkeletonBlocks";
import { SilhouettePlate } from "../shared/KioskSilhouette";
import { Machine } from "../../models/machinesModels";

const plateCardStyle: React.CSSProperties = {
  margin: "12px 14px 0",
  border: "1px solid var(--tier-accent-line)",
  borderRadius: 12,
  boxShadow: "inset 0 0 0 3px var(--card), inset 0 0 0 4px var(--tier-accent-line), var(--shadow)",
  background: "var(--card)",
  overflow: "hidden",
  flexShrink: 0,
};

interface SpecRow {
  key: string;
  label: string;
  value: React.ReactNode;
}

function buildSpecRows(m: Machine, translate: (k: string) => string): SpecRow[] {
  const rows: SpecRow[] = [];
  if (m.serialNumber) rows.push({ key: "serial", label: translate("serialNumber"), value: m.serialNumber });
  if (m.internalNumber) rows.push({ key: "machineNo", label: translate("machineNumber"), value: m.internalNumber });
  const brand = getBrandDisplayName(m);
  if (brand) rows.push({ key: "brand", label: translate("brand"), value: brand });
  if (m.type?.name) rows.push({ key: "type", label: translate("machineType"), value: m.type.name });

  // Combined System row + version-alone fix (PG-5 / bug #9). Gate: csBrand || sysLine.
  const csBrand = m.controlSystemBrand?.name;
  const sysLine = [m.system, m.version].filter(Boolean).join(" ");
  if (csBrand || sysLine) {
    rows.push({
      key: "system",
      label: translate("system"),
      value: (
        <>
          <div>{csBrand || sysLine}</div>
          {csBrand && sysLine && (
            <div style={{ fontSize: 10.5, fontWeight: 400, color: "var(--muted)", marginTop: 1 }}>{sysLine}</div>
          )}
        </>
      ),
    });
  }

  if (m.operationType?.name) rows.push({ key: "operation", label: translate("operationType"), value: m.operationType.name });
  if (m.axisConfiguration?.name) rows.push({ key: "axis", label: translate("axisConfig"), value: m.axisConfiguration.name });
  const year = getProductionYearDisplay(m);
  if (year) rows.push({ key: "year", label: translate("productionYear"), value: year });
  return rows;
}

function SpecSheetSkeleton() {
  return (
    <div style={{ ...plateCardStyle, border: "1px solid var(--border)", boxShadow: "var(--shadow)" }}>
      <SkeletonBlock w="100%" h={170} r={0} />
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
        <SkeletonBlock w="55%" h={14} />
        <SkeletonBlock w="40%" h={10} style={{ marginTop: 5 }} />
      </div>
      <div style={{ padding: "2px 5px 8px" }}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 11px",
              borderTop: i ? "1px solid var(--border-soft)" : "none",
            }}
          >
            <SkeletonBlock w={80} h={8} />
            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <SkeletonBlock w="35%" h={11} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SpecSheetV2() {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");

  useEffect(() => {
    if (id) dispatch(getMachine(id));
  }, [id, dispatch]);

  // Route-scoped machine (KV2-1-e) via the shared resolver (resolves + backfills the
  // guest's curated payload; see useKioskRouteMachine).
  const m = useKioskRouteMachine(id);
  const st = useSurfaceState("machine", id);

  // Viewer banner (PG-4) — v1 parity, local state, direct API. Fails toward hiding.
  const [isViewer, setIsViewer] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  useEffect(() => {
    setIsViewer(false);
    setOwnerName("");
    if (m?.id) {
      publicMachinesApi
        .getPublicMachine(m.id)
        .then((profile) => {
          setIsViewer(!profile.isOwner);
          if (profile.customerName) setOwnerName(profile.customerName);
        })
        .catch(() => setIsViewer(false));
    }
  }, [m?.id]);

  // §5 tree: no route-valid machine → full error only when THIS key failed, else
  // skeleton (BUG-1). Route-valid machine → normal, or cached-dimmed plate on refetch fail.
  let body: React.ReactNode;
  if (!m) {
    body = st.failed ? (
      <ErrorCallout
        title={translate("kioskV2DashboardLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => id && dispatch(getMachine(id))}
        retryLabel={translate("kioskV2Retry")}
      />
    ) : (
      <SpecSheetSkeleton />
    );
  } else {
    const imgSrc = m.image?.content || m.catalogModel?.imageDetailUrl || m.catalogImageUrl || undefined;
    const subtitle = getMachineName(m);
    const rows = buildSpecRows(m, translate);

    const content = (
      <div className="kv2-enter" style={plateCardStyle}>
        {/* (a) Viewer banner (PG-4) */}
        {isViewer && ownerName && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              background: "color-mix(in oklab, var(--accent) 8%, var(--card))",
              borderBottom: "1px solid color-mix(in oklab, var(--accent) 30%, var(--border))",
            }}
          >
            <span style={{ color: "var(--accent)", display: "inline-flex" }}>
              <Glyph d={GD.info} size={16} />
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "color-mix(in oklab, var(--accent) 80%, var(--ink))" }}>
              {translate("viewingMachine", { company: ownerName })}
            </span>
          </div>
        )}

        {/* (b) Media band — imageDetailUrl surfaced, edge-to-edge cover (KV2-4-j) */}
        <div style={{ width: "100%", height: 170, borderBottom: "1px solid var(--border)" }}>
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={getMachineDisplayName(m)}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          ) : (
            <SilhouettePlate w="100%" h="100%" note={translate("kioskV2NoPhoto")} radius={0} />
          )}
        </div>

        {/* (c) Identity row */}
        <div style={{ display: "flex", flexDirection: "column", gap: 1, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>{getMachineDisplayName(m)}</div>
          {subtitle && <div style={{ fontSize: 12, color: "var(--muted)" }}>{subtitle}</div>}
        </div>

        {/* (d) Spec rows */}
        <div style={{ padding: "2px 5px 8px" }}>
          {rows.map((r, i) => (
            <div
              key={r.key}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                padding: "10px 11px",
                borderTop: i ? "1px solid var(--border-soft)" : "none",
              }}
            >
              <span className="eyebrow" style={{ color: "var(--muted)", width: 110, flexShrink: 0 }}>
                {r.label}
              </span>
              <div
                style={{
                  flex: 1,
                  textAlign: "right",
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink)",
                  overflowWrap: "anywhere",
                }}
              >
                {r.value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );

    // Cached machine + refetch failed → dim the whole plate (incl. viewer banner) under
    // the committed callout + Retry.
    body = st.failed ? (
      <CachedErrorState
        title={translate("kioskV2DashboardLoadFailedTitle")}
        hint={translate("kioskV2DashboardLoadFailedHint")}
        onRetry={() => id && dispatch(getMachine(id))}
        retryLabel={translate("kioskV2Retry")}
        cachedAt={st.loadedAt}
        cachedLabel={st.loadedAt != null ? translate("kioskV2ShowingCachedFrom", { time: formatClockTime(st.loadedAt) }) : undefined}
      >
        {content}
      </CachedErrorState>
    ) : (
      content
    );
  }

  return (
    <KPage titleKey="kioskMachineDetails" glyph={GD.info} machineNumber={id}>
      {body}
    </KPage>
  );
}

export default SpecSheetV2;
